import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LayoutTemplate, Plus, Pencil, Trash2, ExternalLink, Loader2 } from "lucide-react";

export default function AdminPages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = () => {
    setLoading(true);
    api.get("/admin/pages")
      .then((r) => setPages(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const deletePage = async (id) => {
    if (!window.confirm("Delete this page?")) return;
    await api.delete(`/admin/pages/${id}`);
    fetch();
    toast.success("Page deleted");
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-pages-list">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl flex items-center gap-2">
            <LayoutTemplate className="w-7 h-7 text-primary" />
            CMS Pages
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Create custom landing pages with the page builder</p>
        </div>
        <Button onClick={() => navigate("/admin/pages/new")} data-testid="new-page-btn">
          <Plus className="w-4 h-4 mr-1" /> New Page
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-xl" />)}</div>
      ) : pages.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground">
          <LayoutTemplate className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No pages yet. Create your first custom page.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="pages-grid">
          {pages.map((p) => (
            <Card key={p.id} className="p-4 flex items-center justify-between gap-4" data-testid={`page-row-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm">{p.title}</span>
                  <Badge variant={p.status === "published" ? "default" : "secondary"} className="text-[10px]">
                    {p.status}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  /p/{p.slug} · {p.blocks?.length || 0} blocks · {fmt(p.updated_at || p.created_at)}
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                {p.status === "published" && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => window.open(`/p/${p.slug}`, "_blank")}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/admin/pages/${p.id}`)} data-testid={`edit-page-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deletePage(p.id)} data-testid={`delete-page-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
